/**
 * Reading a stored result: the service's JSON is `unknown` until it is narrowed.
 *
 * Every result family parses its own rows, and they all start here rather than
 * each keeping a copy — the copies had already drifted on whether a
 * whitespace-only string counts as a value.
 */

export function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** A string with something in it. Whitespace alone is as absent as null. */
export function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

export function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A list, or an empty one when the value is not a list at all. */
export function list(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** The records in a list, dropping whatever is not one. */
export function records(value: unknown): Array<Record<string, unknown>> {
  return list(value).flatMap((entry) => {
    const record = asRecord(entry);
    return record === null ? [] : [record];
  });
}

/** The strings in a list, dropping whatever is not one. */
export function strings(value: unknown): string[] {
  return list(value).flatMap((entry) => text(entry) ?? []);
}

/** A list read as one line, or null when it holds nothing to read. */
export function joined(value: unknown): string | null {
  const parts = strings(value);
  return parts.length === 0 ? null : parts.join(', ');
}
